import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Power, Stethoscope, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

const ICON = { error: <XCircle className="w-3.5 h-3.5 text-rose-400 shrink-0 mt-0.5" />, warn: <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />, ok: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" /> };
const worst = (f) => (f.some((x) => x.level === "error") ? "error" : f.some((x) => x.level === "warn") ? "warn" : "ok");

export function PppBulkDiagDialog({ items, onClose }) {
  const [res, setRes] = useState({});
  const [busy, setBusy] = useState({});

  useEffect(() => {
    let alive = true;
    const queue = [...items];
    const worker = async () => {
      while (queue.length && alive) {
        const it = queue.shift();
        try {
          const { data } = await api.get(`/routers/${it.router_id}/ppp/${encodeURIComponent(it.secret_name)}/diagnosa`);
          if (alive) setRes((p) => ({ ...p, [it.key]: data }));
        } catch (e) {
          if (alive) setRes((p) => ({ ...p, [it.key]: { error: errMsg(e) } }));
        }
      }
    };
    Promise.all([worker(), worker(), worker()]);
    return () => { alive = false; };
  }, [items]);

  const aktifkan = async (it) => {
    setBusy((b) => ({ ...b, [it.key]: true }));
    try {
      await api.post(`/routers/${it.router_id}/ppp/${encodeURIComponent(it.secret_name)}/aktifkan`);
      toast.success(`${it.secret_name} diaktifkan`);
      const { data } = await api.get(`/routers/${it.router_id}/ppp/${encodeURIComponent(it.secret_name)}/diagnosa`);
      setRes((p) => ({ ...p, [it.key]: data }));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy((b) => ({ ...b, [it.key]: false })); }
  };

  const done = Object.keys(res).length;
  const stat = { error: 0, warn: 0, ok: 0, fail: 0 };
  Object.values(res).forEach((r) => { if (r.error) stat.fail++; else stat[worst(r.findings || [])]++; });
  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-bulk-diag">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Stethoscope className="w-5 h-5 text-sky-400" /> Diagnosa Massal — {items.length} pelanggan</DialogTitle>
          <DialogDescription>Membaca bukti langsung dari tiap router (3 sekaligus). Selesai {done}/{items.length} — <span className="text-rose-400">{stat.error} bermasalah</span>, <span className="text-amber-400">{stat.warn} perlu cek</span>, <span className="text-emerald-400">{stat.ok} normal</span>{stat.fail ? <>, <span className="text-muted-foreground">{stat.fail} router gagal</span></> : null}.</DialogDescription>
        </DialogHeader>
        <div className="h-1.5 rounded bg-accent overflow-hidden"><div className="h-full bg-sky-500 transition-[width]" style={{ width: `${(done / Math.max(1, items.length)) * 100}%` }} /></div>
        <div className="space-y-2" data-testid="bulk-diag-list">
          {items.map((it) => {
            const r = res[it.key];
            const lvl = r && !r.error ? worst(r.findings || []) : null;
            const border = lvl === "error" ? "border-rose-500/40" : lvl === "warn" ? "border-amber-500/40" : lvl === "ok" ? "border-emerald-500/40" : "border-border";
            const disabled = r?.secret?.disabled === "true";
            return (
              <div key={it.key} className={`rounded-lg border ${border} bg-card/60 p-3`} data-testid={`bulk-diag-${it.secret_name}`}>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-semibold">{it.secret_name}</span>
                  <span className="text-xs text-muted-foreground">{it.router_name}</span>
                  <span className="text-[11px] text-muted-foreground font-mono">{it.tickets.map((t) => `#${t}`).join(", ")}</span>
                  {r?.active?.length > 0 && <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">ONLINE</span>}
                  {r && !r.error && !r.active?.length && <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-400 border border-rose-500/30">OFFLINE</span>}
                  {disabled && <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-400 border-emerald-500/40" disabled={busy[it.key]} onClick={() => aktifkan(it)} data-testid={`bulk-diag-aktifkan-${it.secret_name}`}><Power className="w-3 h-3 mr-1" /> Aktifkan</Button>}
                </div>
                {!r && <div className="text-xs text-muted-foreground mt-1 animate-pulse">Memeriksa router…</div>}
                {r?.error && <div className="text-xs text-rose-400 mt-1">{r.error}</div>}
                {r && !r.error && (
                  <ul className="mt-1.5 space-y-1">
                    {(r.findings || []).filter((f) => f.level !== "ok" || (r.findings || []).every((x) => x.level === "ok")).slice(0, 6).map((f, i) => <li key={i} className="flex items-start gap-1.5 text-xs">{ICON[f.level]}<span>{f.text}</span></li>)}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
